/**
 * The coordinate space the browser and the recognition engine have to agree on.
 *
 * SelectionData.PreviewWidthPixels / PreviewHeightPixels are the only coupling between the
 * two. The engine rasterizes a PDF itself and derives its own scale from those numbers
 * (HybridGeometryEngine computes sx = PreviewWidthPixels / renderedWidth), so the browser
 * may render at whatever resolution it likes for display as long as it declares this space
 * and reports polygon vertices and calibration points in it.
 *
 * The convention, read off real schema-39 packages rather than assumed:
 *
 *   Fannin    MediaBox 2160x3024pt, /Rotate 270  ->  preview 3024x2160, source 6048x4320
 *   Pinewood  MediaBox 1224x792pt,  /Rotate 0    ->  preview 1224x792,  source 2448x1584
 *
 * The preview is the *effective* page size in points - rotation applied - at scale 1.0, and
 * the engine renders at 2x. An earlier note in the corpus asserted page-points x 2.0 with an
 * identity mapping; that was wrong in both the scale and, for rotated pages, the orientation.
 */

export interface Size {
  readonly width: number;
  readonly height: number;
}

export interface Point {
  readonly x: number;
  readonly y: number;
}

/** What the engine multiplies the preview by when it rasterizes a PDF page. */
export const ENGINE_RASTER_SCALE = 2.0;

/**
 * Normalizes a PDF /Rotate value to one of 0, 90, 180, 270. The spec allows any multiple of
 * 90, including negatives, and real files use both -90 and 270 for the same rotation.
 */
export function normalizeRotation(rotationDegrees: number): 0 | 90 | 180 | 270 {
  if (!Number.isFinite(rotationDegrees)) return 0;
  const normalized = ((Math.round(rotationDegrees / 90) * 90) % 360 + 360) % 360;
  return normalized as 0 | 90 | 180 | 270;
}

/**
 * The page as the reader sees it: MediaBox with /Rotate applied, so a quarter turn swaps the
 * axes. This is the size the preview coordinate space uses.
 */
export function effectivePageSize(mediaBox: Size, rotationDegrees: number): Size {
  const rotation = normalizeRotation(rotationDegrees);
  const quarterTurn = rotation === 90 || rotation === 270;
  return quarterTurn
    ? { width: mediaBox.height, height: mediaBox.width }
    : { width: mediaBox.width, height: mediaBox.height };
}

/** Preview size for a PDF page. Effective page points at scale 1.0. */
export function previewSizeForPdfPage(mediaBox: Size, rotationDegrees: number): Size {
  return effectivePageSize(mediaBox, rotationDegrees);
}

/**
 * Preview size for a raster source. Native pixels: there is no point space to convert from,
 * and the engine loads the image at its own resolution.
 */
export function previewSizeForRasterSource(imageSize: Size): Size {
  return { width: imageSize.width, height: imageSize.height };
}

/**
 * Scale converting a preview coordinate into a coordinate in the raster the engine built.
 * Reported so a caller can check its own assumptions against a package's SourcePixelWidth,
 * which is the only place the real number is observable.
 */
export function previewToSourceScale(preview: Size, source: Size): Point {
  return {
    x: source.width / Math.max(1, preview.width),
    y: source.height / Math.max(1, preview.height),
  };
}

/**
 * The calibration result: how many real-world inches one preview pixel represents. Two
 * points the user clicked, and the distance they say lies between them.
 *
 * Returns null when the points coincide, which is what a stray double-click produces and
 * would otherwise divide by zero and scale the drawing to infinity.
 */
export function inchesPerPreviewPixel(from: Point, to: Point, realWorldInches: number): number | null {
  if (!Number.isFinite(realWorldInches) || realWorldInches <= 0) return null;
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const pixels = Math.hypot(dx, dy);
  if (!Number.isFinite(pixels) || pixels <= 0) return null;
  return realWorldInches / pixels;
}

/**
 * Rotation in the desktop application is display-only: MainWindow applies a RotateTransform
 * to the visual and never passes an angle to Recognize, so stored polygon and calibration
 * coordinates are always in unrotated preview space. The browser has to behave the same way,
 * which means converting a click on a rotated canvas back before storing it.
 */
export function previewToRotatedView(point: Point, preview: Size, viewRotationDegrees: number): Point {
  switch (normalizeRotation(viewRotationDegrees)) {
    case 0:
      return point;
    case 90:
      return { x: preview.height - point.y, y: point.x };
    case 180:
      return { x: preview.width - point.x, y: preview.height - point.y };
    case 270:
      return { x: point.y, y: preview.width - point.x };
  }
}

/** Inverse of {@link previewToRotatedView}. This is the one the click handler needs. */
export function rotatedViewToPreview(point: Point, preview: Size, viewRotationDegrees: number): Point {
  switch (normalizeRotation(viewRotationDegrees)) {
    case 0:
      return point;
    case 90:
      return { x: point.y, y: preview.height - point.x };
    case 180:
      return { x: preview.width - point.x, y: preview.height - point.y };
    case 270:
      return { x: preview.width - point.y, y: point.x };
  }
}
